/**
 * Environment variable validation utility
 * Validates required environment variables on application startup
 */

const REQUIRED_ENV_VARS = [
  'VITE_APP_NAME',
  'VITE_APP_VERSION',
  'VITE_APP_ENV',
  'VITE_API_BASE_URL',
];

const OPTIONAL_ENV_VARS = [
  'VITE_ENABLE_MOCK_API',
  'VITE_ENABLE_DEBUG_MODE',
  'VITE_ENABLE_ANALYTICS',
  'VITE_ANALYTICS_ID',
  'VITE_SENTRY_DSN',
];

/**
 * Validates that all required environment variables are present
 * @returns {Object} Validation result with success status and missing variables
 */
export const validateEnvironment = () => {
  const missing = [];
  const warnings = [];

  // Check required variables
  REQUIRED_ENV_VARS.forEach((varName) => {
    if (!import.meta.env[varName]) {
      missing.push(varName);
    }
  });

  // Check optional variables (just warn)
  OPTIONAL_ENV_VARS.forEach((varName) => {
    if (!import.meta.env[varName]) {
      warnings.push(varName);
    }
  });

  const isValid = missing.length === 0;

  if (!isValid) {
    console.error('❌ Missing required environment variables:', missing);
    console.error('Please check your .env file and ensure all required variables are set.');
  }

  if (warnings.length > 0 && import.meta.env.DEV) {
    console.warn('⚠️ Optional environment variables not set:', warnings);
  }

  return {
    isValid,
    missing,
    warnings,
  };
};

/**
 * Gets an environment variable with a fallback value
 * @param {string} key - Environment variable key
 * @param {any} defaultValue - Default value if not found
 * @returns {string} Environment variable value or default
 */
export const getEnvVar = (key, defaultValue = '') => {
  const value = import.meta.env[key];
  
  if (value === undefined) {
    if (import.meta.env.DEV) {
      console.warn(`Environment variable ${key} is not defined, using default: ${defaultValue}`);
    }
    return defaultValue;
  }
  
  return value;
};

/**
 * Checks if the app is running in development mode
 * @returns {boolean}
 */
export const isDevelopment = () => {
  return import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'development';
};

/**
 * Checks if the app is running in production mode
 * @returns {boolean}
 */
export const isProduction = () => {
  return import.meta.env.PROD || import.meta.env.VITE_APP_ENV === 'production';
};

/**
 * Logs environment information (development only)
 */
export const logEnvironmentInfo = () => {
  if (!isDevelopment()) return;

  console.group('🔧 Environment Information');
  console.log('Mode:', import.meta.env.MODE);
  console.log('Environment:', import.meta.env.VITE_APP_ENV);
  console.log('API Base URL:', import.meta.env.VITE_API_BASE_URL);
  console.log('Mock API Enabled:', import.meta.env.VITE_ENABLE_MOCK_API);
  console.log('Debug Mode:', import.meta.env.VITE_ENABLE_DEBUG_MODE);
  console.groupEnd();
};

// Validate environment on module load
const validation = validateEnvironment();

if (!validation.isValid && isProduction()) {
  throw new Error(
    `Application cannot start: Missing required environment variables: ${validation.missing.join(', ')}`
  );
}

export default {
  validateEnvironment,
  getEnvVar,
  isDevelopment,
  isProduction,
  logEnvironmentInfo,
};
